import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    FileText, Clock, CheckCircle2, Users,
    Calendar, Building2, Hash, User,
    TrendingUp, AlertCircle, FileSpreadsheet,
    CalendarDays, UserCheck, UserX, CheckSquare
} from 'lucide-react';

import InboxHeader from '../../components/Inbox/InboxHeader';
import StatsCards from '../../components/Inbox/StatsCards';
import ActionButtons from '../../components/Inbox/ActionButtons';
import RemarksHistory from '../../components/Inbox/RemarksHistory';
import InboxSplitLayout from '../../components/Inbox/InboxSplitLayout';
import VerificationInput from '../../components/Inbox/VerificationInput';

import {
    fetchVerifyExcelAttendance,
    fetchExcelAttendanceDetails,
    approveExcelAttendance,
    setSelectedAttendanceId,
    setSelectedRoleId,
    resetExcelAttendanceDetails,
    resetMonthlyAttendanceData,
    clearApprovalResult,
    selectVerifyExcelAttendanceInboxArray,
    selectExcelAttendanceDetails,
    selectVerifyExcelAttendanceLoading,
    selectExcelAttendanceDetailsLoading,
    selectApproveExcelAttendanceLoading,
    selectVerifyExcelAttendanceError,
    selectExcelAttendanceDetailsError,
    selectApprovalResult
} from '../../slices/HRSlice/monthlyAttendanceSlice';

import {
    fetchRemarks,
    selectRemarks,
    selectRemarksLoading,
    setSelectedMOID
} from '../../slices/supplierPOSlice/purcahseHelperSlice';

import {
    fetchStatusList,
    selectEnabledActions,
    selectHasActions,
    selectStatusListLoading,
    selectStatusListError,
    resetApprovalData,
    setShowReturnButton
} from '../../slices/CommonSlice/getStatusSlice';

const VerifyExcelAttendance = ({ notificationData, onNavigate }) => {
    const dispatch = useDispatch();

    // Selectors
    const attendanceInbox = useSelector(selectVerifyExcelAttendanceInboxArray);
    const inboxLoading = useSelector(selectVerifyExcelAttendanceLoading);
    const inboxError = useSelector(selectVerifyExcelAttendanceError);

    const attendanceDetails = useSelector(selectExcelAttendanceDetails);
    const detailsLoading = useSelector(selectExcelAttendanceDetailsLoading);
    const detailsError = useSelector(selectExcelAttendanceDetailsError);

    const approvalLoading = useSelector(selectApproveExcelAttendanceLoading);
    const approvalResult = useSelector(selectApprovalResult);

    const remarks = useSelector(selectRemarks);
    const remarksLoading = useSelector(selectRemarksLoading);

    const statusLoading = useSelector(selectStatusListLoading);
    const statusError = useSelector(selectStatusListError);
    const enabledActions = useSelector(selectEnabledActions);
    const hasActions = useSelector(selectHasActions);

    const { userData, userDetails } = useSelector((state) => state.auth);
    const roleId = userData?.roleId || userData?.RID;
    const uid = userData?.UID || userData?.uid;

    // Local State
    const [selectedItem, setSelectedItem] = useState(null);
    const [isVerified, setIsVerified] = useState(false);
    const [verificationComment, setVerificationComment] = useState('');
    const [showRemarksHistory, setShowRemarksHistory] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterCostCenter, setFilterCostCenter] = useState('All');
    const [filterMonth, setFilterMonth] = useState('All');
    const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false);
    const [isLeftPanelHovered, setIsLeftPanelHovered] = useState(false);

    const { InboxTitle, ModuleDisplayName } = notificationData || {};

    // Extract unique values for filters
    const costCenters = [...new Set(attendanceInbox.map(item => item.CostCenter))].filter(Boolean);
    const months = [...new Set(attendanceInbox.map(item => item.AttendanceDate))].filter(Boolean);

    const getCurrentUser = () => {
        return userData?.userName || userDetails?.userName || 'system';
    };

    const getCurrentRoleName = () => {
        return userDetails?.roleName || userData?.roleName ||
            notificationData?.InboxTitle ||
            notificationData?.ModuleDisplayName ||
            'Attendance Verifier';
    };

    const formatApprovalComment = (roleName, userName, comment) => {
        return `${roleName} : ${userName} : ${comment}`;
    };

    const updateRemarksHistory = (existingRemarks, newRoleName, newUserName, newComment) => {
        const formattedNewComment = formatApprovalComment(newRoleName, newUserName, newComment);
        if (!existingRemarks || existingRemarks.trim() === '') {
            return formattedNewComment;
        }
        return `${existingRemarks.trim()}||${formattedNewComment}`;
    };

    /**
     * Extract Month Name and Year from AttendanceDate
     * @param {string} attendanceDate - Format: "November-2025"
     * @returns {object} - { monthName: "November", year: "2025" }
     */
    const extractMonthYear = (attendanceDate) => {
        if (!attendanceDate) return { monthName: '', year: '' };

        const parts = attendanceDate.split('-');
        return {
            monthName: parts[0] || '',
            year: parts[1] || ''
        };
    };

    // Initialize - Fetch attendance inbox
    useEffect(() => {
        if (roleId) {
            console.log('🎯 Initializing Excel Attendance Verification with RoleID:', roleId);
            dispatch(setSelectedRoleId(roleId));
            dispatch(fetchVerifyExcelAttendance(roleId));
        }
    }, [roleId, dispatch]);

    useEffect(() => {
        dispatch(setShowReturnButton('Yes'));
        return () => {
            dispatch(resetMonthlyAttendanceData());
            dispatch(resetApprovalData());
            dispatch(clearApprovalResult());
        };
    }, [dispatch]);

    // Fetch attendance details when item is selected
    // Fetch attendance details when item is selected
    useEffect(() => {
        if (selectedItem?.AttendanceId) {
            const { monthName, year } = extractMonthYear(selectedItem.AttendanceDate);

            console.log('🔍 Fetching Excel Attendance Details:', {
                attendanceId: selectedItem.AttendanceId,
                roleId,
                costCenter: selectedItem.CostCenter,
                month: monthName,
                year: year
            });

            dispatch(setSelectedAttendanceId(selectedItem.AttendanceId));

            // Build payload matching backend requirements - EXACTLY as in Postman
            const detailsPayload = {
                CostCenter: selectedItem.CostCenter || '',
                GenerateFor: selectedItem.GenerateFor || 'Month',
                AttendanceDate: selectedItem.AttendanceDate || '',
                MonthName: monthName,
                Year: year,
                ExcelRefno: selectedItem.ExcelRefno || '',
                AttendanceId: selectedItem.AttendanceId?.toString() || '',
                TransactionNo: selectedItem.TransactionNo || '',
                CCName: selectedItem.CCName || '',
                LabourType: selectedItem.LabourType || '',
                ContractorCode: selectedItem.ContractorCode || '',
                ContractorName: selectedItem.ContractorName || '',
                RoleId: roleId // Add RoleId here
            };

            console.log('📤 Complete Details Payload:', JSON.stringify(detailsPayload, null, 2));

            // Pass the complete payload directly
            dispatch(fetchExcelAttendanceDetails(detailsPayload));

            setIsVerified(false);
            setVerificationComment('');
            setShowRemarksHistory(false);
        }
    }, [selectedItem, roleId, dispatch]);

    // Fetch status list when attendance details are loaded
    useEffect(() => {
        if (selectedItem && roleId && attendanceDetails) {
            // Use MOID from details, fallback to 183 if not found
            const moid = attendanceDetails?.MOID || 183;

            console.log('📊 Fetching Status List for MOID:', moid);
            dispatch(fetchStatusList({
                MOID: moid,
                ROID: roleId,
                ChkAmt: 0
            }));
        }
    }, [selectedItem, roleId, attendanceDetails, dispatch]);

    // Fetch remarks history
    useEffect(() => {
        if (selectedItem && attendanceDetails) {
            // Use MOID from details, fallback to 183 if not found
            const moid = attendanceDetails?.MOID || 183;

            console.log('💬 Fetching Remarks for MOID:', moid);
            dispatch(setSelectedMOID(moid));
            dispatch(fetchRemarks({
                trno: attendanceDetails.ExcelRefno || selectedItem.TransactionNo || '',
                moid: moid
            }));
        }
    }, [selectedItem, attendanceDetails, dispatch]);

    useEffect(() => {
        if (selectedItem) {
            setIsLeftPanelCollapsed(true);
        }
    }, [selectedItem]);

    const handleBackToInbox = () => {
        if (onNavigate) {
            onNavigate('dashboard', { name: 'Dashboard', type: 'dashboard' });
        }
    };

    const handleRefresh = () => {
        if (roleId) {
            console.log('🔄 Refreshing Excel Attendance list');
            dispatch(fetchVerifyExcelAttendance(roleId));

            if (selectedItem) {
                const { monthName, year } = extractMonthYear(selectedItem.AttendanceDate);

                const detailsPayload = {
                    CostCenter: selectedItem.CostCenter || '',
                    GenerateFor: selectedItem.GenerateFor || 'Month',
                    AttendanceDate: selectedItem.AttendanceDate || '',
                    MonthName: monthName,
                    Year: year,
                    ExcelRefno: selectedItem.ExcelRefno || '',
                    AttendanceId: selectedItem.AttendanceId?.toString() || '',
                    TransactionNo: selectedItem.TransactionNo || '',
                    CCName: selectedItem.CCName || '',
                    LabourType: selectedItem.LabourType || '',
                    ContractorCode: selectedItem.ContractorCode || '',
                    ContractorName: selectedItem.ContractorName || ''
                };

                dispatch(fetchExcelAttendanceDetails({
                    id: selectedItem.AttendanceId,
                    roleId,
                    ...detailsPayload
                }));
            }
        }
    };

    const handleItemSelect = (item) => {
        console.log('✅ Selected Excel Attendance Item:', item);
        setSelectedItem(item);
    };
   const buildApprovalPayload = (actionValue) => {
    const currentUser = getCurrentUser();
    const currentRoleName = getCurrentRoleName();

    const updatedRemarks = updateRemarksHistory(
        attendanceDetails?.Remarks || '',
        currentRoleName,
        currentUser,
        verificationComment.trim()
    );

    // Convert attendance data to JSON string
    const attendanceJsonData = JSON.stringify(attendanceDetails?.lstAttendanceData || []);

    // Get labour type and contractor code
    const labourType = attendanceDetails?.LabourType || selectedItem?.LabourType || '';
    const contractorCode = attendanceDetails?.ContractorCode || selectedItem?.ContractorCode || '';

    // Log labour type information
    console.log('👷 Labour Type:', labourType);
    console.log('🏢 Contractor Code:', contractorCode || '(Empty - Own Labour)');

    const payload = {
        AttendanceJson: attendanceJsonData, 
        CostCenter: attendanceDetails?.CostCenter || selectedItem?.CostCenter || '', 
        GenerateFor: attendanceDetails?.GenerateFor || selectedItem?.GenerateFor || 'Month', 
        RoleId: roleId,
        Createdby: currentUser,
        Remarks: updatedRemarks,
        ExcelRefno: attendanceDetails?.ExcelRefno || selectedItem?.ExcelRefno || '',
        LabourType: labourType,
        ContractorCode: contractorCode 
    };

    console.log('📤 Excel Attendance Approval Payload:', payload);
    console.log('📊 Attendance JSON Data Preview:', attendanceJsonData.substring(0, 200) + '...');
    console.log(`✅ Payload ready for ${labourType} - Contractor: ${contractorCode || 'N/A'}`);

    return payload;
};
    const handleActionClick = async (action) => {
        if (!selectedItem) {
            toast.error('No attendance record selected');
            return;
        }

        if (!verificationComment || verificationComment.trim() === '') {
            toast.error('Verification comment is mandatory. Please add your comments before proceeding.');
            return;
        }

        if (!isVerified) {
            toast.error('Please verify the attendance details by checking the verification checkbox.');
            return;
        }

        let actionValue = action.value || action.text || action.type;

        if (!actionValue || actionValue.trim() === '') {
            const typeToValueMap = {
                'approve': 'Approve',
                'verify': 'Verify',
                'reject': 'Reject',
                'return': 'Return'
            };
            actionValue = typeToValueMap[action.type?.toLowerCase()] || 'Verify';
        }

        try {
            const payload = buildApprovalPayload(actionValue);

            const result = await dispatch(approveExcelAttendance(payload)).unwrap();

            if (result && typeof result === 'string') {
                if (result.includes('$')) {
                    const [status, additionalInfo] = result.split('$');
                    toast.success(`${action.text || actionValue} completed successfully!`);
                    if (additionalInfo) {
                        setTimeout(() => {
                            toast.info(additionalInfo, { autoClose: 6000 });
                        }, 500);
                    }
                } else {
                    toast.success(result || `${action.text || actionValue} completed successfully!`);
                }
            } else {
                toast.success(`${action.text || actionValue} completed successfully!`);
            }

            setTimeout(() => {
                dispatch(fetchVerifyExcelAttendance(roleId));
                setSelectedItem(null);
                setVerificationComment('');
                setIsVerified(false);
                setShowRemarksHistory(false);
                setIsLeftPanelCollapsed(false);
                dispatch(resetExcelAttendanceDetails());
                dispatch(resetApprovalData());
                dispatch(clearApprovalResult());
            }, 1000);

        } catch (error) {
            console.error('❌ Approval Error:', error);

            let errorMessage = `Failed to ${action.text?.toLowerCase() || actionValue.toLowerCase()}`;

            if (error && typeof error === 'string') {
                errorMessage = error;
            } else if (error?.message) {
                errorMessage = error.message;
            } else if (error?.response?.data?.message) {
                errorMessage = error.response.data.message;
            }

            toast.error(errorMessage, { autoClose: 10000 });
        }
    };

    const filteredItems = attendanceInbox.filter(item => {
        const matchesSearch = searchQuery === '' ||
            item.TransactionNo?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.ExcelRefno?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.CostCenter?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.CCName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.AttendanceDate?.toLowerCase().includes(searchQuery.toLowerCase());

        const matchesCostCenter = filterCostCenter === 'All' || item.CostCenter === filterCostCenter;
        const matchesMonth = filterMonth === 'All' || item.AttendanceDate === filterMonth;

        return matchesSearch && matchesCostCenter && matchesMonth;
    });

    const statsCards = [
        {
            icon: FileSpreadsheet,
            value: attendanceInbox.length,
            label: 'Total Records',
            color: 'blue'
        },
        {
            icon: Clock,
            value: attendanceInbox.length,
            label: 'Pending Verification',
            color: 'purple'
        },
        {
            icon: Users,
            value: attendanceDetails?.lstAttendanceData?.length || 0,
            label: 'Total Employees',
            color: 'indigo'
        },
        {
            icon: Building2,
            value: attendanceDetails?.CostCenter || '-',
            label: 'Cost Center',
            color: 'violet'
        }
    ];

    const renderItemCard = (item, isSelected) => {
        return (
            <div className="p-4">
                <div className="flex items-center space-x-3 mb-3">
                    <div className="relative">
                        <div className="w-12 h-12 rounded-full border-2 border-blue-200 dark:border-blue-600 bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-800/50 dark:to-purple-800/50 flex items-center justify-center">
                            <FileSpreadsheet className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-purple-500 rounded-full border-2 border-white dark:border-gray-800"></div>
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 dark:text-white text-sm truncate">
                            {item.CCName || item.CostCenter}
                        </h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {item.TransactionNo}
                        </p>
                    </div>
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                    <div className="flex items-center justify-between">
                        <span className="flex items-center space-x-1">
                            <Building2 className="w-3 h-3" />
                            <span>{item.CostCenter}</span>
                        </span>
                        <span className="flex items-center space-x-1">
                            <Calendar className="w-3 h-3" />
                            <span>{item.AttendanceDate}</span>
                        </span>
                    </div>
                    <div className="flex items-center justify-between">
                        <span className="flex items-center space-x-1">
                            <Hash className="w-3 h-3" />
                            <span>ID: {item.AttendanceId}</span>
                        </span>
                        <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full text-xs font-medium">
                            {item.GenerateFor || 'Month'}
                        </span>
                    </div>
                    {item.LabourType && (
                        <div className="flex items-center space-x-1">
                            <Users className="w-3 h-3" />
                            <span>{item.LabourType}</span>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderCollapsedItem = (item, isSelected) => (
        <div className="w-full h-full rounded-lg border-2 border-blue-200 dark:border-blue-600 bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-800/50 dark:to-purple-800/50 flex items-center justify-center">
            <FileSpreadsheet className="w-4 h-4 text-blue-600 dark:text-blue-400" />
        </div>
    );

    // Compact single-line row for the "classic" list view — same fields as
    // renderItemCard, laid out horizontally instead of stacked.
    const renderListItem = (item) => (
        <div className="flex items-center gap-x-6 gap-y-1 flex-wrap text-sm">
            <span className="font-semibold text-gray-900 dark:text-white min-w-[160px] truncate">
                {item.CCName || item.CostCenter}
            </span>
            <span className="text-gray-500 dark:text-gray-400 min-w-[110px] truncate">
                {item.TransactionNo}
            </span>
            <span className="flex items-center gap-1 text-gray-500 dark:text-gray-400 min-w-[100px]">
                <Calendar className="w-3 h-3" />
                {item.AttendanceDate}
            </span>
            <span className="flex items-center gap-1 text-gray-500 dark:text-gray-400 min-w-[90px]">
                <Hash className="w-3 h-3" />
                ID: {item.AttendanceId}
            </span>
            {item.LabourType && (
                <span className="flex items-center gap-1 text-gray-500 dark:text-gray-400 min-w-[90px]">
                    <Users className="w-3 h-3" />
                    {item.LabourType}
                </span>
            )}
            <span className="ml-auto px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full text-xs font-medium whitespace-nowrap">
                {item.GenerateFor || 'Month'}
            </span>
        </div>
    );

    const renderAttendanceTable = () => {
        if (!attendanceDetails?.lstAttendanceData || attendanceDetails.lstAttendanceData.length === 0) {
            return null;
        }

        // Get all day columns (2-30)
        const dayColumns = Array.from({ length: 29 }, (_, i) => (i + 2).toString());

        return (
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border-2 border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                        <div className="p-2 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg">
                            <CalendarDays className="w-5 h-5 text-white" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                            Monthly Attendance Sheet
                        </h3>
                    </div>
                    <div className="flex items-center space-x-4 text-xs">
                        <div className="flex items-center space-x-2">
                            <div className="w-3 h-3 rounded bg-green-500"></div>
                            <span className="text-gray-600 dark:text-gray-400">P - Present</span>
                        </div>
                        <div className="flex items-center space-x-2">
                            <div className="w-3 h-3 rounded bg-red-500"></div>
                            <span className="text-gray-600 dark:text-gray-400">A - Absent</span>
                        </div>
                        <div className="flex items-center space-x-2">
                            <div className="w-3 h-3 rounded bg-blue-500"></div>
                            <span className="text-gray-600 dark:text-gray-400">S - Sunday/Off</span>
                        </div>
                    </div>
                </div>

                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20">
                            <tr>
                                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider sticky left-0 bg-blue-50 dark:bg-blue-900/20 z-10">
                                    #
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider sticky left-12 bg-blue-50 dark:bg-blue-900/20 z-10 min-w-[200px]">
                                    Employee Name
                                </th>
                                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                                    Group
                                </th>
                                {dayColumns.map(day => (
                                    <th key={day} className="px-2 py-3 text-center text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider min-w-[40px]">
                                        {day}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {attendanceDetails.lstAttendanceData.map((employee, index) => (
                                <tr key={employee.LabourId || index} className="hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-colors">
                                    <td className="px-3 py-3 text-sm text-gray-700 dark:text-gray-300 sticky left-0 bg-white dark:bg-gray-800">
                                        {employee.Rowno || index + 1}
                                    </td>
                                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white sticky left-12 bg-white dark:bg-gray-800">
                                        <div>
                                            <p className="font-semibold">{employee.Name}</p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">{employee.LabourId}</p>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-center">
                                        <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded text-xs font-medium">
                                            {employee.GroupName}
                                        </span>
                                    </td>
                                    {dayColumns.map(day => {
                                        const status = employee[day];
                                        let bgColor = 'bg-gray-100 dark:bg-gray-700';
                                        let textColor = 'text-gray-600 dark:text-gray-400';

                                        if (status === 'P') {
                                            bgColor = 'bg-green-100 dark:bg-green-900/30';
                                            textColor = 'text-green-700 dark:text-green-300';
                                        } else if (status === 'A') {
                                            bgColor = 'bg-red-100 dark:bg-red-900/30';
                                            textColor = 'text-red-700 dark:text-red-300';
                                        } else if (status === 'S') {
                                            bgColor = 'bg-blue-100 dark:bg-blue-900/30';
                                            textColor = 'text-blue-700 dark:text-blue-300';
                                        }

                                        return (
                                            <td key={day} className="px-2 py-3 text-center">
                                                <span className={`inline-flex items-center justify-center w-8 h-8 rounded font-medium text-xs ${bgColor} ${textColor}`}>
                                                    {status || '-'}
                                                </span>
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    const renderDetailContent = () => {
        if (!selectedItem) return null;

        const displayData = attendanceDetails || selectedItem;
        const hasDetailedData = !!attendanceDetails;

        return (
            <div className="space-y-6">
                {detailsLoading && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-700">
                        <div className="flex items-center space-x-3">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                            <span className="text-blue-700 dark:text-blue-400 text-sm">
                                Loading attendance details...
                            </span>
                        </div>
                    </div>
                )}

                {/* CUSTOM HEADER */}
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-2xl p-6 border-2 border-blue-200 dark:border-blue-700">
                    <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-4">
                            <div className="relative">
                                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg">
                                    <FileSpreadsheet className="w-8 h-8 text-white" />
                                </div>
                                <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-purple-500 rounded-full border-3 border-white dark:border-gray-800 flex items-center justify-center">
                                    <AlertCircle className="w-4 h-4 text-white" />
                                </div>
                            </div>

                            <div className="flex-1">
                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                                    {displayData.CCName || displayData.CostCenter}
                                </h2>
                                <p className="text-blue-600 dark:text-blue-400 font-semibold mb-3">
                                    {displayData.AttendanceDate} • ID: {displayData.AttendanceId}
                                </p>

                                <div className="flex flex-wrap gap-2">
                                    <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-xs font-medium">
                                        Excel Attendance
                                    </span>
                                    <span className="px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full text-xs font-medium">
                                        {displayData.GenerateFor || 'Month'}
                                    </span>
                                    {displayData.LabourType && (
                                        <span className="px-3 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-full text-xs font-medium">
                                            {displayData.LabourType}
                                        </span>
                                    )}
                                    {displayData.DataStatus && (
                                        <span className="px-3 py-1 bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 rounded-full text-xs font-medium">
                                            {displayData.DataStatus}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {hasDetailedData && attendanceDetails.lstAttendanceData && (
                            <div className="text-right">
                                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Total Employees</p>
                                <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                                    {attendanceDetails.lstAttendanceData.length}
                                </p>
                                {displayData.ExcelRefno && (
                                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                                        Ref: {displayData.ExcelRefno}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="grid grid-cols-4 gap-4 mt-6 pt-6 border-t border-blue-200 dark:border-blue-700">
                        {hasDetailedData && displayData.MOID && (
                            <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">MOID</p>
                                <p className="text-sm font-bold text-gray-900 dark:text-white">
                                    {displayData.MOID}
                                </p>
                            </div>
                        )}
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Transaction No</p>
                            <p className="text-sm font-bold text-gray-900 dark:text-white">
                                {displayData.TransactionNo}
                            </p>
                        </div>
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Cost Center</p>
                            <p className="text-sm font-bold text-gray-900 dark:text-white">
                                {displayData.CostCenter}
                            </p>
                        </div>
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Excel Ref</p>
                            <p className="text-sm font-bold text-gray-900 dark:text-white">
                                {displayData.ExcelRefno}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Attendance Table */}
                {hasDetailedData && renderAttendanceTable()}

                {/* Contractor Information */}
                {hasDetailedData && (displayData.ContractorCode || displayData.ContractorName) && (
                    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border-2 border-gray-200 dark:border-gray-700">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
                            <User className="w-5 h-5 text-purple-600" />
                            <span>Contractor Information</span>
                        </h3>
                        <div className="grid grid-cols-2 gap-4">
                            {displayData.ContractorCode && (
                                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Contractor Code</p>
                                    <p className="text-base font-semibold text-gray-900 dark:text-white">
                                        {displayData.ContractorCode}
                                    </p>
                                </div>
                            )}
                            {displayData.ContractorName && (
                                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Contractor Name</p>
                                    <p className="text-base font-semibold text-gray-900 dark:text-white">
                                        {displayData.ContractorName}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <RemarksHistory
                    isOpen={showRemarksHistory}
                    onToggle={() => setShowRemarksHistory(!showRemarksHistory)}
                    remarks={remarks}
                    loading={remarksLoading}
                    title="Approval History"
                />

                <VerificationInput
                    isVerified={isVerified}
                    onVerifiedChange={setIsVerified}
                    comment={verificationComment}
                    onCommentChange={(e) => setVerificationComment(e.target.value)}
                    config={{
                        checkboxLabel: '✓ I have verified all attendance details',
                        checkboxDescription: 'Including employee attendance data, cost center information, and contractor details',
                        commentLabel: 'Verification Comments',
                        commentPlaceholder: 'Please verify attendance records, employee data accuracy, and any discrepancies...',
                        commentRequired: true,
                        commentRows: 4,
                        commentMaxLength: 1000,
                        showCharCount: true,
                        validationStyle: 'dynamic',
                        checkboxGradient: 'from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20',
                        commentGradient: 'from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20',
                        commentBorder: 'border-blue-200 dark:border-blue-700'
                    }}
                />

                {statusLoading ? (
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-center space-x-3">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                            <span className="text-gray-600 dark:text-gray-400">Loading actions...</span>
                        </div>
                    </div>
                ) : statusError ? (
                    <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 border border-red-200 dark:border-red-700">
                        <p className="text-red-600 dark:text-red-400 text-center">
                            ⚠️ Error loading actions: {statusError}
                        </p>
                    </div>
                ) : !hasActions || !enabledActions || enabledActions.length === 0 ? (
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-xl p-6 border border-yellow-200 dark:border-yellow-700">
                        <p className="text-yellow-700 dark:text-yellow-400 text-center">
                            ℹ️ No actions available for this attendance record
                        </p>
                    </div>
                ) : (
                    <ActionButtons
                        actions={enabledActions}
                        onActionClick={handleActionClick}
                        loading={approvalLoading}
                        isVerified={isVerified}
                        comment={verificationComment}
                        showValidation={true}
                        excludeActions={['send back']}
                    />
                )}
            </div>
        );
    };

    return (
        <div className="space-y-6">
            <InboxHeader
                title={`${InboxTitle || 'Excel Attendance Verification'} (${attendanceInbox.length})`}
                subtitle={ModuleDisplayName}
                itemCount={attendanceInbox.length}
                onBackClick={handleBackToInbox}
                HeaderIcon={FileSpreadsheet}
                badgeText="Excel Attendance"
                enableViewToggle
                badgeCount={attendanceInbox.length}
                searchConfig={{
                    enabled: true,
                    placeholder: 'Search by transaction no, cost center, month...',
                    value: searchQuery,
                    onChange: (e) => setSearchQuery(e.target.value)
                }}
                filters={[
                    {
                        value: filterCostCenter,
                        onChange: (e) => setFilterCostCenter(e.target.value),
                        defaultLabel: 'All Cost Centers',
                        options: costCenters
                    },
                    {
                        value: filterMonth,
                        onChange: (e) => setFilterMonth(e.target.value),
                        defaultLabel: 'All Months',
                        options: months
                    }
                ]}
            />

            <div className="px-6 -mt-auto mb-6">
                <StatsCards
                    cards={statsCards}
                    variant="simple"
                    gridCols="grid-cols-1 md:grid-cols-4"
                    gap="gap-4"
                />
            </div>

            <InboxSplitLayout
                isLeftPanelCollapsed={isLeftPanelCollapsed}
                onLeftPanelCollapseToggle={setIsLeftPanelCollapsed}
                isLeftPanelHovered={isLeftPanelHovered}
                onLeftPanelHoverChange={setIsLeftPanelHovered}
                left={{
                    items: filteredItems,
                    selectedItem: selectedItem,
                    onItemSelect: handleItemSelect,
                    renderItem: renderItemCard,
                    renderListItem: renderListItem,
                    renderCollapsedItem: renderCollapsedItem,
                    loading: inboxLoading,
                    error: inboxError,
                    onRefresh: handleRefresh,
                    config: {
                        title: 'Pending Verification',
                        icon: Clock,
                        emptyMessage: 'No attendance records found!',
                        itemKey: 'AttendanceId',
                        enableCollapse: true,
                        enableRefresh: true,
                        enableHover: true,
                        maxHeight: '100%',
                        headerGradient: 'from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20',
                    },
                    renderPopupContent: (_item) => renderDetailContent(),
                    popupConfig: {
                        title: 'Excel Attendance Verification',
                        icon: FileSpreadsheet,
                        headerGradient: 'from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20',
                        maxWidth: 'max-w-[80vw]',
                    },
                }}
                right={{
                    selectedItem: selectedItem,
                    loading: false,
                    renderContent: renderDetailContent,
                    config: {
                        title: 'Attendance Details',
                        icon: FileSpreadsheet,
                        selectedTitle: 'Attendance Verification',
                        emptyTitle: 'No Attendance Selected',
                        emptyMessage: 'Select an attendance record from the list to view details and verify.',
                        headerGradient: 'from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20',
                        maxHeight: 'calc(100vh - 200px)',
                        sticky: true,
                        stickyTop: '1.5rem',
                    },
                }}
            />
        </div>
    );
};

export default VerifyExcelAttendance;